import { Button } from "@/components/ui/button";
import type { SocialAccount } from "@/hooks/use-accounts";
import type { AccountHealthRow } from "@/hooks/use-account-health";
import { PlatformIcon } from "@/components/social/PlatformIcon";
import { RefreshCw, Unlink, Plug } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import type { TFunction } from "i18next";

const STATUS_STYLE: Record<SocialAccount["status"], string> = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  EXPIRED: "bg-amber-100 text-amber-700",
  ERROR: "bg-red-100 text-red-700",
  DISCONNECTED: "bg-gray-100 text-gray-600",
};

const HEALTH_DOT: Record<NonNullable<AccountHealthRow["status"]>, string> = {
  healthy: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-rose-500",
};

interface Props {
  account: SocialAccount;
  health: AccountHealthRow | null;
  onSync: () => void;
  onDisconnect: () => void;
  onReconnect?: () => void;
  syncing?: boolean;
  canEdit: boolean;
}

function expiresInPill(tokenExpiresAt: string | null, t: TFunction): string | null {
  if (!tokenExpiresAt) return null;
  const ms = new Date(tokenExpiresAt).getTime() - Date.now();
  if (ms < 0) return t("socialAccount.expired");
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days <= 7) return t("socialAccount.expiresIn", { days });
  return null;
}

export function SocialAccountRow({
  account,
  health,
  onSync,
  onDisconnect,
  onReconnect,
  syncing,
  canEdit,
}: Props) {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const dot = health?.status ? HEALTH_DOT[health.status] : "bg-gray-300";
  const expiresPill = expiresInPill(health?.tokenExpiresAt ?? null, t);
  const needsReconnect =
    health?.status === "error" ||
    (health?.tokenExpiresAt !== null &&
      health?.tokenExpiresAt !== undefined &&
      new Date(health.tokenExpiresAt).getTime() < Date.now());

  return (
    <div className="rounded-lg border p-3 flex items-center gap-3">
      <PlatformIcon platform={account.platform} size={32} className="rounded" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn("h-2 w-2 rounded-full inline-block", dot)}
            aria-label={t("socialAccount.healthAriaLabel", { status: health?.status ?? t("socialAccount.healthUnknown") })}
          />
          <span className="truncate text-sm font-medium">
            {account.username ?? t("socialAccount.noHandle")}
          </span>
          {expiresPill && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase text-amber-700">
              {expiresPill}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{account.platform}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${STATUS_STYLE[account.status]}`}
          >
            {t(`socialAccount.status.${account.status}`)}
          </span>
          <span>
            {account.lastVerifiedAt
              ? t("socialAccount.verifiedOn", { date: formatDate(new Date(account.lastVerifiedAt), locale, { dateStyle: "medium" }) })
              : t("socialAccount.neverVerified")}
          </span>
        </div>
        {health?.recommendation && health.status !== "healthy" && (
          <div className="mt-1 text-xs text-rose-700" title={health.recommendation}>
            {health.recommendation}
          </div>
        )}
      </div>
      <div className="flex gap-1">
        {needsReconnect && onReconnect && (
          <Button
            size="sm"
            variant="outline"
            onClick={onReconnect}
            disabled={!canEdit}
            aria-label={t("socialAccount.reconnect")}
          >
            <Plug className="h-4 w-4 mr-1" />
            {t("socialAccount.reconnect")}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onSync}
          disabled={!canEdit || syncing}
          aria-label={t("socialAccount.sync")}
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDisconnect}
          disabled={!canEdit}
          aria-label={t("socialAccount.disconnect")}
        >
          <Unlink className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
